import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy, Component, EventEmitter, Inject, Input, OnChanges, Optional, Output, SimpleChanges, TemplateRef,
  ViewChild, computed, signal
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Grid, GridConfig, GridExportRequestedEvent, GridHyperlinkClickEvent, GridRow } from '@inavista/components/grid';
import { APP_CONFIG, AppConfig } from '../../../../app-config.token';
import { LoaderComponent } from '../../../../components/loader/loader.component';
import { LocalRowsGridApi, provideLocalGridRows } from '../../../../utils/local-rows-grid-api';
import { DroDataType, DroFormField, DroReading } from '../../model/dro.model';
import { exportDroGrid } from '../../utils/dro-export';
import { formatDroDate, formatDroValue } from '../../utils/dro-validation';
import { openDroReadingPopup } from '../dro-reading-popup/dro-reading-popup.component';

interface DroFieldColumn {
  id: string;
  label: string;
  isNumber: boolean;
}

const URL = 'page://dro-history';
const FIELD_PREFIX = 'f_';
const FIXED = ['readingId', 'readingDate', 'recordedBy', 'source'];

/**
 * Reading history for one serial, shared by the Equipment and Service Order DRO tabs, on @inavista/components: four fixed
 * columns, then one column per form field in form order. Codes found only in older readings (removed from the form since)
 * follow at the end. The rows come with the tab, so the grid sorts, filters and searches in the browser (LocalRowsGridApi).
 */
@Component({
  selector: 'lib-dro-reading-history',
  templateUrl: './dro-reading-history.component.html',
  styleUrl: './dro-reading-history.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule, Grid, LoaderComponent],
  providers: [...provideLocalGridRows()]
})
export class DroReadingHistoryComponent implements OnChanges {
  @Input() readings: DroReading[] = [];
  @Input() fields: DroFormField[] = [];
  @Input() serialNumber = '';
  @Input() loading = false;

  @Output() readingDeleted = new EventEmitter<string>();
  @Output() serviceOrderClick = new EventEmitter<string>();

  @ViewChild('grid') grid: Grid;
  @ViewChild('recordedByCell', { static: true }) set recordedByCellRef(ref: TemplateRef<unknown>) { this.recordedByCell.set(ref); }
  @ViewChild('sourceCell', { static: true }) set sourceCellRef(ref: TemplateRef<unknown>) { this.sourceCell.set(ref); }

  private readonly recordedByCell = signal<TemplateRef<unknown> | null>(null);
  private readonly sourceCell = signal<TemplateRef<unknown> | null>(null);
  private readonly fieldColumns = signal<DroFieldColumn[]>([]);
  private readonly persistenceKey: string;

  /** The grid reads its columns once: it is recreated when the field columns change. */
  readonly columnsKey = computed(() => this.fieldColumns().map(c => `${c.id}:${c.label}:${c.isNumber}`).join('|'));
  collapsed = false;

  readonly config = computed<GridConfig | null>(() => {
    const recordedBy = this.recordedByCell(), source = this.sourceCell();
    if (!recordedBy || !source) {
      return null;
    }
    const fields = this.fieldColumns();
    const value = (v: unknown, row: GridRow, id: string) =>
      v === undefined ? '' : formatDroValue(v as number | string | null, (row['decimals'] as Record<string, number>)?.[id] ?? 0);
    return {
      apiBaseUrl: URL,
      clientSide: true,
      keyColumn: 'readingId',
      rowHeight: 26,
      stretchColumns: true,
      enableContextMenuFilter: true,
      enableColumnsPicker: true,
      enableResetLayout: true,
      enableExport: true,
      enableFooter: false,
      defaultColumnOrder: [...FIXED, ...fields.map(f => f.id)],
      hiddenByDefaultColumns: ['roles', 'serviceOrderId'],
      alwaysVisibleColumns: ['readingId'],
      defaultSort: { column: 'readingDate', direction: 'desc' },
      columnLabels: {
        readingId: 'Reading ID', readingDate: 'Date', recordedBy: 'Recorded By', roles: 'Role', source: 'Source',
        serviceOrderId: 'Service Order', ...Object.fromEntries(fields.map(f => [f.id, f.label]))
      },
      columnWidths: { readingId: 120, readingDate: 115, recordedBy: 190, source: 130, ...Object.fromEntries(fields.map(f => [f.id, 120])) },
      // Headers left; ids centred; numbers right; dates and text left (RIMS grid look).
      columnAlign: Object.fromEntries([...FIXED, ...fields.map(f => f.id)].map(c => [c, {
        header: { horizontal: 'left' as const },
        ...(c === 'readingId' || c === 'source' ? { horizontal: 'center' as const } : {}),
        ...(fields.find(f => f.id === c)?.isNumber ? { horizontal: 'right' as const } : {})
      }])),
      columnFormatters: {
        // The business day, never shifted into the viewer's time zone.
        readingDate: v => (v ? formatDroDate(String(v).slice(0, 10)) : ''),
        ...Object.fromEntries(fields.map(f => [f.id, (v: unknown, row: GridRow) => value(v, row, f.id)]))
      },
      hyperlinkColumns: ['readingId'],
      cellTemplates: { recordedBy, source },
      excelFilterColumns: ['recordedBy', 'source'],
      emptyStateMessage: 'No readings recorded for this serial yet.',
      persistenceKey: this.persistenceKey,
      persistenceVersion: 2
    };
  });

  constructor(
    private dialog: MatDialog,
    private gridRows: LocalRowsGridApi,
    @Optional() @Inject(APP_CONFIG) appConfig: AppConfig | null
  ) {
    this.persistenceKey = appConfig?.appName === 'sales' ? 'rims.sales.dro-history' : 'rims.service.dro-history';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['fields'] || changes['readings']) {
      const columns = this.buildFieldColumns();
      this.gridRows.set(URL, LocalRowsGridApi.schema([
        ['readingId', 'nvarchar'], ['readingDate', 'date'], ['recordedBy', 'nvarchar'], ['roles', 'nvarchar'],
        ['source', 'nvarchar'], ['serviceOrderId', 'nvarchar'],
        ...columns.map(c => [c.id, c.isNumber ? 'decimal' : 'nvarchar'] as [string, string])
      ]), this.readings.map(r => this.toRow(r)));
      if (columns.map(c => `${c.id}:${c.label}:${c.isNumber}`).join('|') !== this.columnsKey()) {
        this.fieldColumns.set(columns);
      } else {
        this.grid?.refresh({ keepScroll: true });
      }
    }
  }

  onLink(event: GridHyperlinkClickEvent): void {
    if (event.columnId === 'readingId') {
      this.openReading(String(event.row['readingId']));
    }
  }

  openReading(readingId: string): void {
    openDroReadingPopup(this.dialog, readingId).subscribe(result => {
      if (result?.deleted) {
        this.readingDeleted.emit(readingId);
      }
    });
  }

  onServiceOrderClick(serviceOrderId: string): void {
    this.serviceOrderClick.emit(serviceOrderId);
  }

  onExport(event: GridExportRequestedEvent): void {
    if (this.grid) {
      exportDroGrid(this.grid, event, `DRO Readings ${this.serialNumber}`);
    }
  }

  private buildFieldColumns(): DroFieldColumn[] {
    const columns = [...this.fields]
      .sort((a, b) => a.position - b.position)
      .map(f => this.toColumn(f.fieldCode, f.label, f.dataType));

    const known = new Set(this.fields.map(f => f.fieldCode));
    this.readings.forEach(r => r.values.forEach(v => {
      if (!known.has(v.fieldCode)) {
        known.add(v.fieldCode);
        columns.push(this.toColumn(v.fieldCode, v.label, v.dataType));
      }
    }));
    return columns;
  }

  private toColumn(fieldCode: string, label: string, dataType: DroDataType): DroFieldColumn {
    return { id: FIELD_PREFIX + fieldCode, label, isNumber: dataType === DroDataType.number };
  }

  private toRow(reading: DroReading): GridRow {
    const decimals: Record<string, number> = {};
    const row: GridRow = {
      readingId: reading.readingId,
      readingDate: `${reading.readingDate.slice(0, 10)}T00:00:00`,
      recordedBy: reading.recordedBy,
      roles: reading.recordedByRoles.join(' / '),
      source: reading.serviceOrderId ?? 'Standalone',
      serviceOrderId: reading.serviceOrderId,
      decimals
    };
    reading.values.forEach(v => {
      row[FIELD_PREFIX + v.fieldCode] = v.value;
      decimals[FIELD_PREFIX + v.fieldCode] = v.decimalPlaces;
    });
    return row;
  }
}
